import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { 
  ArrowLeft, Package, FileText, Clock, CheckCircle, Truck, 
  Loader2, Calendar, DollarSign, MapPin, Palette, Hash
} from 'lucide-react';
import { brandName, brandEmail, brandPhone, brandLogo, brandWebsite } from '../lib/brand';

const CustomerOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('customerToken');
    if (!token) {
      navigate('/portal/login');
      return;
    }
    fetchOrderDetail();
  }, [orderId]);

  const fetchOrderDetail = async () => {
    try {
      const token = localStorage.getItem('customerToken');
      const response = await axios.get(`/customer/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setData(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('customerToken');
        navigate('/portal/login');
      } else if (error.response?.status === 403) {
        toast.error('Access denied');
        navigate('/portal');
      } else {
        toast.error('Failed to load order');
        navigate('/portal');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  // Order status timeline
  const statuses = ['Pending', 'Ordered', 'In Production', 'Shipped', 'Delivered'];
  
  const getStatusIndex = (status) => {
    return statuses.indexOf(status);
  };

  const getStatusColor = (status, isActive) => {
    if (!isActive) return '#e5e7eb';
    const colors = {
      'Pending': '#f59e0b',
      'Ordered': '#3b82f6',
      'In Production': '#8b5cf6',
      'Shipped': '#10b981',
      'Delivered': '#059669'
    };
    return colors[status] || '#6b7280';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'Pending': Clock,
      'Ordered': FileText,
      'In Production': Package,
      'Shipped': Truck,
      'Delivered': CheckCircle
    };
    return icons[status] || Clock;
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <Loader2 size={40} style={{ color: '#e74c3c', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  const order = data?.order;
  const proposal = data?.proposal;
  const currentStatusIndex = getStatusIndex(order?.status);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc' }}>
      {/* Header */}
      <header style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
        padding: '16px 24px'
      }}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <Link 
            to="/portal"
            style={{ 
              color: '#9ca3af', 
              textDecoration: 'none',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '14px',
              marginBottom: '12px'
            }}
          >
            <ArrowLeft size={16} />
            Back to Portal
          </Link>
          <h1 style={{ color: 'white', margin: 0, fontSize: '24px', fontWeight: '600' }}>
            Order {order?.po_number}
          </h1>
        </div>
      </header>

      <main style={{ maxWidth: '900px', margin: '0 auto', padding: '24px' }}>
        {/* Status Timeline */}
        <div style={{
          background: 'white',
          borderRadius: '16px',
          padding: '32px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          marginBottom: '24px'
        }}>
          <h2 style={{ 
            fontSize: '16px', 
            fontWeight: '600', 
            color: '#1a1a2e',
            marginBottom: '24px'
          }}>
            Order Status
          </h2>

          {/* Timeline */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'space-between',
            position: 'relative',
            padding: '0 20px'
          }}>
            {/* Progress Line Background */}
            <div style={{
              position: 'absolute',
              top: '24px',
              left: '60px',
              right: '60px',
              height: '4px',
              background: '#e5e7eb',
              borderRadius: '2px'
            }} />
            
            {/* Progress Line Active */}
            <div style={{
              position: 'absolute',
              top: '24px',
              left: '60px',
              width: `${(currentStatusIndex / (statuses.length - 1)) * (100 - 13.3)}%`,
              height: '4px',
              background: getStatusColor(order?.status, true),
              borderRadius: '2px',
              transition: 'width 0.5s ease'
            }} />

            {statuses.map((status, index) => {
              const isActive = index <= currentStatusIndex;
              const isCurrent = index === currentStatusIndex;
              const StatusIcon = getStatusIcon(status);
              const color = getStatusColor(status, isActive);

              return (
                <div 
                  key={status}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center',
                    position: 'relative',
                    zIndex: 1
                  }}
                >
                  <div style={{
                    width: isCurrent ? '56px' : '48px',
                    height: isCurrent ? '56px' : '48px',
                    borderRadius: '50%',
                    background: isActive ? color : '#f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'all 0.3s ease',
                    boxShadow: isCurrent ? `0 0 0 4px ${color}30` : 'none'
                  }}>
                    <StatusIcon 
                      size={isCurrent ? 26 : 22} 
                      color={isActive ? 'white' : '#9ca3af'} 
                    />
                  </div>
                  <p style={{
                    marginTop: '12px',
                    fontSize: '13px',
                    fontWeight: isCurrent ? '600' : '400',
                    color: isActive ? '#1a1a2e' : '#9ca3af',
                    textAlign: 'center'
                  }}>
                    {status}
                  </p>
                  {isCurrent && (
                    <span style={{
                      fontSize: '11px',
                      color: color,
                      fontWeight: '500',
                      marginTop: '4px'
                    }}>
                      Current
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {/* Tracking Number */}
          {(order?.tracking_number || order?.fulfillment?.tracking_number) && (
            <div style={{
              marginTop: '32px',
              padding: '20px',
              background: '#ecfdf5',
              borderRadius: '12px',
              textAlign: 'center'
            }} data-testid="customer-tracking-box">
              <p style={{ color: '#065f46', fontSize: '14px', margin: '0 0 8px' }}>
                <MapPin size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                {order.fulfillment?.shipping_status === 'delivered' ? 'Delivered' : order.fulfillment?.shipping_status === 'shipped' ? 'Shipped' : 'Tracking Number'}{order.fulfillment?.carrier ? ` · ${order.fulfillment.carrier.toUpperCase()}` : ''}
              </p>
              <p style={{ 
                fontSize: '24px', 
                fontWeight: '700', 
                color: '#047857',
                margin: 0,
                letterSpacing: '2px'
              }}>
                {order.fulfillment?.tracking_number || order.tracking_number}
              </p>
              {order.fulfillment?.tracking_url && <a href={order.fulfillment.tracking_url} target="_blank" rel="noreferrer" style={{ display: 'inline-block', marginTop: 10, background: '#047857', color: '#fff', padding: '8px 16px', borderRadius: 8, fontWeight: 700, textDecoration: 'none', fontSize: 14 }} data-testid="customer-tracking-link">Track package</a>}
            </div>
          )}
        </div>

        {/* Order Details */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '16px',
          marginBottom: '24px'
        }}>
          {/* Order Info */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6b7280',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Order Information
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>PO Number</span>
                <span style={{ fontWeight: '600', color: '#1a1a2e' }}>{order?.po_number}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Order Date</span>
                <span style={{ fontWeight: '500', color: '#1a1a2e' }}>{formatDate(order?.created_at)}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Quantity</span>
                <span style={{ fontWeight: '500', color: '#1a1a2e' }}>{order?.quantity} pcs</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Order Type</span>
                <span style={{ fontWeight: '500', color: '#1a1a2e' }}>{order?.order_type}</span>
              </div>
              {order?.eta && (
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>Est. Completion</span>
                  <span style={{ fontWeight: '500', color: '#1a1a2e' }}>{formatDate(order?.eta)}</span>
                </div>
              )}
            </div>
          </div>

          {/* Payment Info */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6b7280',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Payment Details
            </h3>
            <div style={{ 
              fontSize: '32px', 
              fontWeight: '700', 
              color: '#1a1a2e',
              marginBottom: '16px'
            }}>
              {formatCurrency(order?.total_amount)}
            </div>
            <div style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              background: '#dcfce7',
              color: '#166534',
              padding: '6px 12px',
              borderRadius: '20px',
              fontSize: '13px',
              fontWeight: '600'
            }}>
              <CheckCircle size={14} />
              Paid
            </div>
          </div>
        </div>

        {/* Items */}
        {proposal?.items?.length > 0 && (
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
          }}>
            <h3 style={{ 
              fontSize: '14px', 
              fontWeight: '600', 
              color: '#6b7280',
              marginBottom: '16px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px'
            }}>
              Order Items
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {proposal.items.map((item, index) => (
                <div 
                  key={index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    padding: '16px',
                    background: '#f9fafb',
                    borderRadius: '10px'
                  }}
                >
                  {item.image_url ? (
                    <img 
                      src={item.image_url} 
                      alt={item.style}
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '8px'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '60px',
                      height: '60px',
                      background: '#e5e7eb',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Package size={24} color="#9ca3af" />
                    </div>
                  )}
                  <div style={{ flex: 1 }}>
                    <p style={{ 
                      fontWeight: '600', 
                      color: '#1a1a2e', 
                      margin: '0 0 4px',
                      fontSize: '15px'
                    }}>
                      {item.brand} {item.style}
                    </p>
                    <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                      <span style={{ 
                        fontSize: '13px', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Palette size={14} />
                        {item.color}
                      </span>
                      <span style={{ 
                        fontSize: '13px', 
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}>
                        <Hash size={14} />
                        {Object.entries(item.sizes || {}).map(([size, qty]) => `${size}:${qty}`).join(', ')}
                      </span>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: '600', color: '#1a1a2e', margin: 0 }}>
                      {formatCurrency(item.markup_price)}
                    </p>
                    <p style={{ fontSize: '12px', color: '#6b7280', margin: '2px 0 0' }}>
                      per unit
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Need Help */}
        <div style={{
          marginTop: '24px',
          padding: '24px',
          background: '#fef3c7',
          borderRadius: '12px',
          textAlign: 'center'
        }}>
          <p style={{ color: '#92400e', margin: 0, fontSize: '14px' }}>
            Questions about your order? Contact us at{' '}
            <a href={`mailto:${brandEmail()}`} style={{ fontWeight: '600', color: '#92400e' }}>
              {brandEmail()}
            </a>
          </p>
        </div>
      </main>
    </div>
  );
};

export default CustomerOrderDetail;
